import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = authenticated
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    // Only probe /auth/me if a token is stored OR if a session cookie might exist (admin page).
    // Avoids 401 noise in console for anonymous public visitors.
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("kb_token");
    const isAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    if (!hasToken && !isAdminRoute) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) {
        localStorage.setItem("kb_token", data.access_token);
      }
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed (continuing client-side logout):", e);
    }
    localStorage.removeItem("kb_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
